import type { CraftResponseValid, ItemMeta, Rarity } from '@/types/domain';

const RARITY_RANK: Record<Rarity, number> = {
  common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5,
};

export interface ProgressionResult {
  tier: number;
  rarity: Rarity;
  clamped: boolean;
  warnings: string[];
}

/**
 * Estimates/clamps a generated item's progression tier so early recipes cannot
 * trivially produce endgame power. The model proposes a tier; we keep it within
 * one step above the strongest ingredient (configurable via strictness).
 */
export function validateProgression(
  response: CraftResponseValid,
  ingredients: ItemMeta[],
  strictness: number,
): ProgressionResult {
  const warnings: string[] = [];
  const ingTiers = ingredients.map((i) => (i as ItemMeta).tier || 1);
  const maxIng = ingTiers.length ? Math.max(...ingTiers) : 1;

  let tier = Math.max(1, Math.min(5, Math.round(response.tier || maxIng)));

  // strictness 0 = permissive (allow +2), 1 = default (allow +1), 2 = strict (no jump)
  const maxStep = strictness >= 2 ? 0 : strictness >= 1 ? 1 : 2;
  const ceiling = Math.min(5, maxIng + maxStep);
  if (tier > ceiling) {
    warnings.push(`Tier ${tier} exceeds progression ceiling ${ceiling} for these ingredients; clamped.`);
    tier = ceiling;
  }

  // Cap rarity to ingredient rarity +1 step to avoid legendary from dirt.
  const maxIngRarity = ingredients.length
    ? Math.max(...ingredients.map((i) => RARITY_RANK[i.rarity] || 1))
    : 1;
  let rarity = response.rarity;
  const rarityCap = Math.min(5, maxIngRarity + 1);
  if (RARITY_RANK[rarity] > rarityCap) {
    const order: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
    rarity = order[Math.max(0, rarityCap - 1)];
    warnings.push(`Rarity clamped to ${rarity} based on ingredient rarity.`);
  }

  // Forbid world-ending / omnipotent concepts.
  const BAD = /\b(world.?ending|omnipotent|invincible|infinite energy|unlimited|all.?powerful)\b/i;
  if (BAD.test(response.name) || BAD.test(response.description)) {
    warnings.push('Result concept flagged as overpowered; reroll suggested.');
  }

  return {
    tier,
    rarity,
    clamped: warnings.length > 0,
    warnings,
  };
}

export function estimateIngredientTier(metas: ItemMeta[]): number {
  if (!metas.length) return 1;
  return Math.max(...metas.map((m) => m.tier || 1));
}
